import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { createRuleListInlineHandlers } from "../../../../../components/modals/shared/ruleFormConstants";
import { usePaRequestsActionFieldOptions } from "../../../../../components/modals/shared/usePaRequestsActionFieldOptions";
import { useCmmInputUniqueValuesContext } from "../../../../../context/cmmInputUniqueValuesContext";
import { mergePaRequestsMetadataIntoConditionConfig } from "../../../../../data-model/pharmacyOrderUpdatesPaRequestsMetadata";
import { RootState } from "../../../../../redux/store/store";
import type { CallingRuleTrigger } from "../../callingConfiguration/callingRuleUtils";
import {
  buildFirstStpReviewConditionConfigFromCmmColumns,
  createEmptyConditionBlockLayout,
  createEmptyFirstStpReviewRule,
  DEFAULT_FIRST_STP_DOC_TRIGGERS,
  FIRST_STP_REVIEW_CONDITION_FIELD_OPTIONS,
  FIRST_STP_REVIEW_CONDITION_TITLE,
  getDefaultFirstStpBlockLayout,
  getDefaultFirstStpReviewRules,
  mapFirstStpReviewRuleStatesToFirestorePayload,
  parseFirstStpReviewDoc,
  resolveFirstStpDocTriggers,
  type FirstStpConditionBlockLayout,
  type FirstStpReviewRuleDoc,
  type FirstStpReviewRuleState,
} from "./firstStpReviewRuleUtils";
import {
  getStpConfigDocId,
  getStpConfigDocumentPath,
  type StpVariant,
} from "./useFirstStpConfig";

const cloneRules = (
  rules: FirstStpReviewRuleState[],
): FirstStpReviewRuleState[] =>
  JSON.parse(JSON.stringify(rules)) as FirstStpReviewRuleState[];

const cloneTriggers = (triggers: CallingRuleTrigger[]): CallingRuleTrigger[] =>
  JSON.parse(JSON.stringify(triggers)) as CallingRuleTrigger[];

const cloneBlockLayout = (
  layout: FirstStpConditionBlockLayout[],
): FirstStpConditionBlockLayout[] =>
  JSON.parse(JSON.stringify(layout)) as FirstStpConditionBlockLayout[];

/**
 * Reads the document-level `enabled` boolean, defaulting to on when the field is absent.
 *
 * @param doc - Loaded no-touch config, or null when the org has no document yet
 * @returns Whether second STP review is enabled
 */
const resolveDocEnabled = (doc: FirstStpReviewRuleDoc | null): boolean =>
  typeof doc?.enabled === "boolean" ? doc.enabled : true;

/**
 * Manages editable STP review rules at `api_config/{orgId}/no-touch-config/latest`
 * (first STP), `…/second_stp_review` (second STP), or `…/agentic`.
 *
 * @param firestoreDoc - Loaded no-touch config for the current org, or null
 * @param configLoading - True while config for `orgId` is not yet ready
 * @param orgId - Selected organization id
 * @param loadedForOrgId - Org id the fetched `firestoreDoc` belongs to; must match `orgId` before apply
 * @param variant - Review stage being edited; decides the document written on save
 */
export function useFirstStpReviewRules(
  firestoreDoc: FirstStpReviewRuleDoc | null,
  configLoading: boolean,
  orgId: string,
  loadedForOrgId: string | null,
  variant: StpVariant = "first",
) {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const appliedDocRef = useRef(false);
  const loadedOrgIdRef = useRef<string | null>(null);
  const [rules, setRules] = useState<FirstStpReviewRuleState[]>(
    getDefaultFirstStpReviewRules(),
  );
  const [triggers, setTriggers] = useState<CallingRuleTrigger[]>(
    cloneTriggers(DEFAULT_FIRST_STP_DOC_TRIGGERS),
  );
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const lastSavedRulesRef = useRef<FirstStpReviewRuleState[]>(
    getDefaultFirstStpReviewRules(),
  );
  const lastSavedTriggersRef = useRef<CallingRuleTrigger[]>(
    cloneTriggers(DEFAULT_FIRST_STP_DOC_TRIGGERS),
  );
  const lastSavedBlockLayoutRef = useRef<FirstStpConditionBlockLayout[]>([]);
  const [enabled, setEnabled] = useState(true);
  const lastSavedEnabledRef = useRef(true);
  const [lastSavedMeta, setLastSavedMeta] = useState<{
    updated_at?: string;
    updated_by?: string;
  } | null>(null);
  const [ruleLoadedVersion, setRuleLoadedVersion] = useState(0);
  const [blockLayout, setBlockLayout] = useState<
    FirstStpConditionBlockLayout[]
  >([]);
  const existingDocRef = useRef<FirstStpReviewRuleDoc | null>(null);

  useEffect(() => {
    const loadKey = `${variant}:${orgId}`;
    if (loadedOrgIdRef.current !== loadKey) {
      appliedDocRef.current = false;
      loadedOrgIdRef.current = loadKey;
      existingDocRef.current = null;
      setIsDirty(false);
    }
  }, [orgId, variant]);

  useEffect(() => {
    if (!orgId.trim() || configLoading || appliedDocRef.current) return;
    if (loadedForOrgId !== orgId) return;
    appliedDocRef.current = true;

    if (firestoreDoc == null) {
      const seed = getDefaultFirstStpReviewRules();
      const defaultLayout = getDefaultFirstStpBlockLayout(seed);
      setBlockLayout(defaultLayout);
      setRules(seed);
      setTriggers(cloneTriggers(DEFAULT_FIRST_STP_DOC_TRIGGERS));
      setEnabled(true);
      lastSavedRulesRef.current = cloneRules(seed);
      lastSavedTriggersRef.current = cloneTriggers(
        DEFAULT_FIRST_STP_DOC_TRIGGERS,
      );
      lastSavedBlockLayoutRef.current = cloneBlockLayout(defaultLayout);
      lastSavedEnabledRef.current = true;
      setRuleLoadedVersion((v) => v + 1);
      return;
    }

    existingDocRef.current = firestoreDoc;
    const parsed = parseFirstStpReviewDoc(firestoreDoc);

    if (parsed.blockLayout.length > 0) {
      setBlockLayout(parsed.blockLayout);
      setRules(parsed.rules);
      lastSavedRulesRef.current = cloneRules(parsed.rules);
      lastSavedBlockLayoutRef.current = cloneBlockLayout(parsed.blockLayout);
    } else if (parsed.rules.length > 0) {
      const layout = getDefaultFirstStpBlockLayout(parsed.rules);
      setBlockLayout(layout);
      setRules(parsed.rules);
      lastSavedRulesRef.current = cloneRules(parsed.rules);
      lastSavedBlockLayoutRef.current = cloneBlockLayout(layout);
    } else {
      const seed = getDefaultFirstStpReviewRules();
      const defaultLayout = getDefaultFirstStpBlockLayout(seed);
      setBlockLayout(defaultLayout);
      setRules(seed);
      lastSavedRulesRef.current = cloneRules(seed);
      lastSavedBlockLayoutRef.current = cloneBlockLayout(defaultLayout);
    }

    const docTriggers = resolveFirstStpDocTriggers(firestoreDoc);
    setTriggers(docTriggers);
    lastSavedTriggersRef.current = cloneTriggers(docTriggers);
    const docEnabled = resolveDocEnabled(firestoreDoc);
    setEnabled(docEnabled);
    lastSavedEnabledRef.current = docEnabled;
    setLastSavedMeta({
      updated_at: firestoreDoc.updated_at,
      updated_by: firestoreDoc.updated_by,
    });
    setRuleLoadedVersion((v) => v + 1);
  }, [firestoreDoc, configLoading, orgId, loadedForOrgId, variant]);

  const markDirty = () => setIsDirty(true);

  const handleAddConditionBlock = () => {
    markDirty();
    setBlockLayout((layout) => [
      ...layout,
      createEmptyConditionBlockLayout(layout.length),
    ]);
  };

  const handleAddRuleToBlock = (blockIndex: number) => {
    markDirty();
    const newRule = createEmptyFirstStpReviewRule(rules.length);
    setRules((prev) => [...prev, newRule]);
    setBlockLayout((layout) => {
      if (layout.length === 0) {
        return [
          { ...createEmptyConditionBlockLayout(0), ruleIds: [newRule.id] },
        ];
      }
      return layout.map((block, index) =>
        index === blockIndex
          ? { ...block, ruleIds: [...block.ruleIds, newRule.id] }
          : block,
      );
    });
  };

  const handleBlockLabelChange = (blockIndex: number, value: string) => {
    markDirty();
    setBlockLayout((layout) =>
      layout.map((block, index) =>
        index === blockIndex ? { ...block, lable: value } : block,
      ),
    );
  };

  const handleBlockSubtextChange = (blockIndex: number, value: string) => {
    markDirty();
    setBlockLayout((layout) =>
      layout.map((block, index) =>
        index === blockIndex ? { ...block, subtext: value } : block,
      ),
    );
  };

  const handleDeleteConditionBlock = (blockIndex: number) => {
    markDirty();
    const ruleIdsToRemove = new Set(blockLayout[blockIndex]?.ruleIds ?? []);
    setRules((prev) => prev.filter((rule) => !ruleIdsToRemove.has(rule.id)));
    setBlockLayout((layout) =>
      layout.filter((_, index) => index !== blockIndex),
    );
  };

  const {
    handleRuleLabelChange,
    handleRuleSubtextChange,
    handleRuleActiveToggle,
    handleRuleConditionChange,
    handleRuleActionFieldChange,
    handleRuleActionTypeChange,
    handleRuleActionStringValueChange,
    handleRuleActionMultiValueChange,
    handleRuleAddAction,
    handleRuleRemoveAction,
  } = createRuleListInlineHandlers(setRules, markDirty);

  const handleDeleteRule = (ruleId: string) => {
    markDirty();
    setRules((prev) => prev.filter((r) => r.id !== ruleId));
    setBlockLayout((layout) =>
      layout.map((block) => ({
        ...block,
        ruleIds: block.ruleIds.filter((id) => id !== ruleId),
      })),
    );
  };

  const handleTriggerTypeChange = (index: number, newType: string) => {
    markDirty();
    setTriggers((prev) =>
      prev.map((t, i) => {
        if (i !== index) return t;
        if (newType === "cron") {
          const schedule =
            (t as { schedule?: string }).schedule ?? "0 12 * * *";
          return { type: "cron", schedule };
        }
        return { type: newType };
      }),
    );
  };

  const handleTriggerScheduleChange = (index: number, schedule: string) => {
    markDirty();
    setTriggers((prev) =>
      prev.map((t, i) =>
        i === index && t.type === "cron" ? { type: "cron", schedule } : t,
      ),
    );
  };

  const handleAddTrigger = () => {
    markDirty();
    setTriggers((prev) => [...prev, { type: "api" }]);
  };

  const handleRemoveTrigger = (index: number) => {
    markDirty();
    setTriggers((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Flips the document-level `enabled` flag used by the Second STP Review tab.
   */
  const handleEnabledToggle = () => {
    markDirty();
    setEnabled((prev) => !prev);
  };

  const { cmmInputUniqueValues } = useCmmInputUniqueValuesContext();
  const {
    metadata: paRequestsMetadata,
    fieldOptions: paRequestsFieldOptions,
    valueConditionConfig,
  } = usePaRequestsActionFieldOptions();

  const conditionConfig = useMemo(() => {
    const pageConfig = buildFirstStpReviewConditionConfigFromCmmColumns(
      cmmInputUniqueValues,
      FIRST_STP_REVIEW_CONDITION_FIELD_OPTIONS,
    );
    const mergedFromMetadata = mergePaRequestsMetadataIntoConditionConfig(
      pageConfig,
      paRequestsMetadata,
      FIRST_STP_REVIEW_CONDITION_FIELD_OPTIONS,
    );
    return mergedFromMetadata ?? pageConfig;
  }, [cmmInputUniqueValues, paRequestsMetadata]);

  const actionFieldOptions = useMemo(
    () =>
      paRequestsFieldOptions.length > 0
        ? paRequestsFieldOptions
        : conditionConfig.fields.map((f) => ({
            value: f.value,
            label: f.label,
          })),
    [paRequestsFieldOptions, conditionConfig],
  );

  const actionValueConfig = useMemo(
    () =>
      valueConditionConfig.fields.length > 0
        ? valueConditionConfig
        : conditionConfig,
    [valueConditionConfig, conditionConfig],
  );

  const configKey = useMemo(
    () =>
      conditionConfig.fields.length > 0
        ? conditionConfig.fields
            .map((f) => f.value)
            .sort()
            .join(",")
        : "empty",
    [conditionConfig],
  );

  const handleSave = async () => {
    if (!orgId.trim()) {
      toast.error("Select an organization before saving.");
      return;
    }

    setSaving(true);
    try {
      const payload = mapFirstStpReviewRuleStatesToFirestorePayload(
        rules,
        triggers,
        existingDocRef.current,
        user?.email ?? undefined,
        blockLayout,
        true,
      );
      const mergedPayload = JSON.parse(
        JSON.stringify({
          ...(existingDocRef.current ?? {}),
          ...payload,
          id: getStpConfigDocId(variant),
          ...(variant === "second" ? { enabled } : {}),
        }),
      ) as FirstStpReviewRuleDoc;
      delete (mergedPayload as Record<string, unknown>).rules;

      await FirestoreService.setDocumentByPath(
        getStpConfigDocumentPath(orgId, variant),
        mergedPayload as Record<string, unknown>,
        { merge: true },
      );
      existingDocRef.current = mergedPayload;
      lastSavedRulesRef.current = cloneRules(rules);
      lastSavedTriggersRef.current = cloneTriggers(triggers);
      lastSavedBlockLayoutRef.current = cloneBlockLayout(blockLayout);
      lastSavedEnabledRef.current = enabled;
      setIsDirty(false);
      setLastSavedMeta({
        updated_at: payload.updated_at,
        updated_by: payload.updated_by ?? user?.email ?? undefined,
      });
      toast.success("Changes saved");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!window.confirm("Are you sure? All unsaved changes will be lost."))
      return;
    setRules(cloneRules(lastSavedRulesRef.current));
    setTriggers(cloneTriggers(lastSavedTriggersRef.current));
    setBlockLayout(cloneBlockLayout(lastSavedBlockLayoutRef.current));
    setEnabled(lastSavedEnabledRef.current);
    setIsDirty(false);
    setRuleLoadedVersion((v) => v + 1);
  };

  return {
    rules,
    triggers,
    blockLayout,
    conditionConfig,
    actionFieldOptions,
    actionValueConfig,
    configKey,
    conditionTitle: FIRST_STP_REVIEW_CONDITION_TITLE,
    isDirty,
    saving,
    ruleLoadedVersion,
    handleAddConditionBlock,
    handleAddRuleToBlock,
    handleBlockLabelChange,
    handleBlockSubtextChange,
    handleDeleteConditionBlock,
    handleRuleLabelChange,
    handleRuleSubtextChange,
    handleRuleActiveToggle,
    handleRuleConditionChange,
    handleRuleActionFieldChange,
    handleRuleActionTypeChange,
    handleRuleActionStringValueChange,
    handleRuleActionMultiValueChange,
    handleRuleAddAction,
    handleRuleRemoveAction,
    handleDeleteRule,
    handleTriggerTypeChange,
    handleTriggerScheduleChange,
    handleAddTrigger,
    handleRemoveTrigger,
    handleSave,
    handleDiscard,
    lastSavedMeta,
    enabled,
    handleEnabledToggle,
  };
}
